import { Outlet, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ProviderContext } from "../../contexts/provider.context";
import AuthenMysa from "../../services/authen/mysa.authen";
import ScoutConnectService from "../../services/hr9/hr9.services";
import tokenService from "../../services/token.service";
import { getCompany, getCompanyCredits, updateUser } from "../../services/api.service";
import ProfileSCModel from "../../model/profile.model";
import INTERVIEW_LOGO from "../../assets/images/logo-interview-ai.png";
import LOGO from "../../assets/images/logo-no-text.webp";
import SyncCompanyLogoModal from "../../modal/sync-company-logo.modal";
import "./layout.component.scss";

/**
 * Strip query strings / fragments / trailing slashes so two URLs that point at
 * the same image (one with a Firebase download token, one without) compare equal.
 * We only need a coarse "do these refer to the same logo asset" check.
 */
function normalizeLogoUrl(url: string): string {
    const trimmed = String(url || "").trim();
    if (!trimmed) return "";
    try {
        const parsed = new URL(trimmed);
        return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, "").toLowerCase();
    } catch {
        return trimmed.split("?")[0].split("#")[0].replace(/\/+$/, "").toLowerCase();
    }
}

const Layout = () => {
    const { company_id } = useParams<{ company_id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { setProfileModel, profileModel, companyCreditsVersion }: any = useContext(ProviderContext);
    const [profileMod, setProfileMod] = useState<ProfileSCModel>(profileModel);
    const [companyCredits, setCompanyCredits] = useState<{
        report_credit_total: number | null;
        report_credit_used: number;
        template_question_credit_total: number | null;
        template_question_credit_used: number;
    } | null>(null);
    // Logo persisted on the Firestore interview_company doc — this is what the
    // public interview funnel renders. We pull it on mount to compare with the
    // MySA-derived logo shown in the header and detect drift.
    const [firestoreLogoUrl, setFirestoreLogoUrl] = useState<string>("");
    const [showLogoSyncModal, setShowLogoSyncModal] = useState(false);

    const updateCompanyUsers = useCallback(() => {
        ScoutConnectService.getTeamManagement().then((res) => {
            if (res.data.content) {
                updateUser(tokenService.getHR9CompanyUUID(), res.data.content).catch(() => undefined);
            }
        });
    }, []);

    const checkProfile = useCallback(async () => {
        try {
            const profileResult = await AuthenMysa().getProfile();
            if (!profileResult) { navigate("/login"); return; }
            const profileModels = profileResult["data"] as ProfileSCModel;
            const companyRes = await getCompany(company_id);
            if (!companyRes.data?.is_success) { navigate("/login"); return; }
            const selected = profileModels.companyManageResponses.filter(
                (item) => item.companyUuid === company_id
            )[0];
            if (!selected) { navigate("/choose-company"); return; }
            profileModels.roleName = selected.roleName;
            tokenService.setProfile(profileModels);
            setProfileModel(profileModels);
            setProfileMod(profileModels);
            const logoFromFirestore = String(companyRes.data?.message?.company_logo || "").trim();
            setFirestoreLogoUrl(logoFromFirestore);
        } catch {
            navigate("/login");
        }
    }, [company_id, navigate, setProfileModel]);

    useEffect(() => {
        updateCompanyUsers();
        checkProfile();
    }, [checkProfile, updateCompanyUsers]);

    useEffect(() => {
        if (!company_id) return;
        getCompanyCredits(company_id)
            .then((res) => {
                if (res.data?.is_success && res.data?.data) setCompanyCredits(res.data.data);
            })
            .catch(() => undefined);
    }, [company_id, companyCreditsVersion]);

    const reportRatio = useMemo(() => {
        if (!companyCredits || typeof companyCredits.report_credit_total !== "number") return 0;
        return Math.min(
            100,
            Math.round(
                (companyCredits.report_credit_used / Math.max(1, companyCredits.report_credit_total)) * 100,
            ),
        );
    }, [companyCredits]);

    const templateRatio = useMemo(() => {
        if (!companyCredits || typeof companyCredits.template_question_credit_total !== "number") return 0;
        return Math.min(
            100,
            Math.round(
                (companyCredits.template_question_credit_used /
                    Math.max(1, companyCredits.template_question_credit_total)) *
                    100,
            ),
        );
    }, [companyCredits]);

    const currentCompany = profileMod?.companyManageResponses?.find((x) => x.companyUuid === company_id);
    const mysaLogoUrl = String(currentCompany?.companyLogoImageUrl || "").trim();
    // Header prefers Firestore (the source-of-truth for what candidates see) and
    // falls back to MySA so existing flows keep working when Firestore is empty.
    const headerLogoUrl = firestoreLogoUrl || mysaLogoUrl || "";

    useEffect(() => {
        if (!company_id) return;
        if (!profileMod) return;
        // Wait until both sides resolve. If MySA has no logo we have nothing to
        // sync from — surface the modal anyway so the admin can re-upload manually.
        const fLogo = normalizeLogoUrl(firestoreLogoUrl);
        const mLogo = normalizeLogoUrl(mysaLogoUrl);
        const mismatch = !fLogo || (mLogo && fLogo !== mLogo);
        if (!mismatch) {
            if (showLogoSyncModal) setShowLogoSyncModal(false);
            return;
        }
        setShowLogoSyncModal(true);
    }, [company_id, profileMod, firestoreLogoUrl, mysaLogoUrl, showLogoSyncModal]);

    const onLogoSynced = useCallback((newUrl: string) => {
        if (newUrl) setFirestoreLogoUrl(newUrl);
        setShowLogoSyncModal(false);
    }, []);

    const base = `/${tokenService.getHR9CompanyUUID()}`;
    const pathname = location.pathname;
    const inQuestionArea =
        pathname.includes("/job-questions-list") ||
        pathname.includes("/interviewers") ||
        pathname.includes("/job/") ||
        pathname.includes("/create-job-questions") ||
        pathname.includes("/create-general-questions") ||
        pathname.includes("/edit-general-questions");
    const inAllQuestionsSubmenu =
        pathname.includes("/job-questions-list") ||
        pathname.includes("/job/") ||
        pathname.includes("/create-job-questions") ||
        pathname.includes("/create-general-questions") ||
        pathname.includes("/edit-general-questions");
    const inSettingsArea =
        pathname.includes("/company-questions-management") ||
        pathname.includes("/invite-hr-users") ||
        pathname.includes("/settings");

    const roleLabel = useMemo(() => {
        const rawRole = String(profileMod?.roleName || "").trim();
        if (!rawRole) return "—";
        return rawRole
            .replace(/^ROLE_/i, "")
            .toLowerCase()
            .split("_")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }, [profileMod?.roleName]);

    return (
        <div className="app-layout">
            <aside className="app-layout-sidebar">
                <div className="als-logo-wrap">
                    <img src={INTERVIEW_LOGO} className="als-logo" alt="Interview AI" />
                </div>

                <nav className="als-nav">
                    {/* Section: รายการคำถาม */}
                    <div className="als-section">
                        <button
                            className={`als-section-head ${inQuestionArea ? "active" : ""}`}
                            onClick={() => navigate(`${base}/job-questions-list`)}
                        >
                            รายการคำถาม
                        </button>
                        <NavLink
                            to={`${base}/job-questions-list`}
                            className={`als-nav-item ${inAllQuestionsSubmenu ? "active" : ""}`}
                        >
                            คำถามทั้งหมด
                        </NavLink>
                        <NavLink to={`${base}/interviewers`} className="als-nav-item">
                            ผู้สัมภาษณ์ทั้งหมด
                        </NavLink>
                    </div>

                    {/* Section: ประวัติการใช้งาน */}
                    <div className="als-section">
                        <NavLink to={`${base}/credit-history`} className="als-section-head">
                            ประวัติการใช้งาน
                        </NavLink>
                    </div>

                    {/* Section: ตั้งค่า */}
                    <div className="als-section">
                        <button
                            className={`als-section-head ${inSettingsArea ? "active" : ""}`}
                            onClick={() => navigate(`${base}/company-questions-management`)}
                        >
                            ตั้งค่า
                        </button>
                        <NavLink to={`${base}/company-questions-management`} className="als-nav-item">
                            ตั้งค่าคำถามองค์กร
                        </NavLink>
                        <NavLink to={`${base}/invite-hr-users`} className="als-nav-item">
                            จัดการผู้ใช้งาน
                        </NavLink>
                        <NavLink
                            to={`${base}/settings`}
                            className={`als-nav-item${pathname.endsWith("/settings") ? " active" : ""}`}
                        >
                            จัดการเครดิต
                        </NavLink>
                    </div>
                </nav>

                {/* Credits */}
                <div className="als-credits">
                    <div className="als-credit-item">
                        <div className="als-credit-row">
                            <span className="als-credit-label">Report Credit</span>
                            <span className="als-credit-value">
                                {companyCredits
                                    ? `${companyCredits.report_credit_used} / ${companyCredits.report_credit_total ?? "∞"}`
                                    : "—"}
                            </span>
                        </div>
                        <div className="als-credit-bar">
                            <div className="fill report" style={{ width: `${reportRatio}%` }} />
                        </div>
                    </div>
                    <div className="als-credit-item">
                        <div className="als-credit-row">
                            <span className="als-credit-label">Template Credit</span>
                            <span className="als-credit-value">
                                {companyCredits
                                    ? `${companyCredits.template_question_credit_used} / ${companyCredits.template_question_credit_total ?? "∞"}`
                                    : "—"}
                            </span>
                        </div>
                        <div className="als-credit-bar">
                            <div className="fill template" style={{ width: `${templateRatio}%` }} />
                        </div>
                    </div>
                </div>

                {/* Logout */}
                <button className="als-switch-company" onClick={() => navigate("/choose-company")}>
                    เปลี่ยนบริษัท
                </button>
                <button className="als-logout" onClick={() => AuthenMysa().LogOut()}>
                    ออกจากระบบ
                </button>
            </aside>

            <main className="app-layout-content">
                <header className="alc-header">
                    <div
                        className="alc-company"
                        onClick={() => navigate("/choose-company")}
                        title="เปลี่ยนบริษัท"
                    >
                        <img
                            src={headerLogoUrl || LOGO}
                            alt=""
                            className="alc-company-logo"
                        />
                        <div>
                            <strong>{currentCompany?.companyName || "Company"}</strong>
                            <span>{roleLabel}</span>
                        </div>
                    </div>
                </header>
                <section className="alc-body">
                    <Outlet />
                </section>
            </main>

            {company_id && (
                <SyncCompanyLogoModal
                    isOpen={showLogoSyncModal}
                    companyId={company_id}
                    mysaLogoUrl={mysaLogoUrl}
                    firestoreLogoUrl={firestoreLogoUrl}
                    companyName={String(currentCompany?.companyName || "")}
                    onSynced={onLogoSynced}
                />
            )}
        </div>
    );
};

export default Layout;
